import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import CachedImage from '@components/CachedImage/CachedImage';
import PlayCircle from '@components/Icons/PlayCircle';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Document, LocalFile } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useUploadFiles } from '@hooks/useUploadFiles';
import theme from '@themes/theme';

interface ImageGridNewProps {
  list: (Document | LocalFile)[];
  onSelect?: (image: Document | LocalFile) => void; // optional legacy single toggle
  onChangeSelectedList?: (next: Array<Document | LocalFile>) => void; // ✅ for scheme B
  onPress?: (image: Document | LocalFile) => void;
  onRetry?: (image: Document | LocalFile) => void;
  onDelete?: (index: number) => void;
  onLongPress?: (image: Document | LocalFile) => void;
  isSelectMode?: boolean;
  setIsSelectMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectedList?: Array<Document | LocalFile>;
}

const ImageGridNew: React.FC<ImageGridNewProps> = ({
  list,
  onSelect,
  onChangeSelectedList,
  onPress,
  onRetry,
  onDelete,
  onLongPress,
  isSelectMode = false,
  setIsSelectMode,
  selectedList,
}) => {
  const { uploadingFiles, uploadingErrorFiles, setUploadingErrorFiles } =
    useUploadFiles();
  const numImages = 4;
  const [bodyWidth, setBodyWidth] = useState(0);
  const [isHoveredIndex, setIsHoveredIndex] = useState(-1);
  const { me } = useMe();
  const { t } = useTranslation();

  type UploadingItem = LocalFile & { progress?: number };
  const [pendingUploads, setPendingUploads] = useState<
    Map<string, UploadingItem>
  >(() => new Map());

  const getUploadKey = (f: any) => f.clientId ?? f.id;
  const getDocKey = (d: any) => d.clientId ?? d.file?.clientId ?? d.id;

  // ✅ Stable key for selection across Document/LocalFile
  const getItemKey = (x: any) => x.clientId ?? x.file?.clientId ?? x.id;

  useEffect(() => {
    setPendingUploads((prev) => {
      const next = new Map(prev);
      uploadingFiles.forEach((f: any) => {
        next.set(getUploadKey(f), f);
      });
      uploadingErrorFiles.forEach((f: any) => {
        next.set(getUploadKey(f), f);
      });
      return next;
    });
  }, [uploadingFiles, uploadingErrorFiles]);

  useEffect(() => {
    const uploadedKeys = new Set(list.map((d: any) => getDocKey(d)));

    setPendingUploads((prev) => {
      const next = new Map(prev);
      for (const key of next.keys()) {
        if (uploadedKeys.has(key)) next.delete(key);
      }
      return next;
    });
  }, [list]);

  const stableUploadingFiles = Array.from(pendingUploads.values());

  const allFiles = useMemo(
    () => [...stableUploadingFiles, ...list],
    [stableUploadingFiles, list]
  );

  useEffect(() => {
    if (isSelectMode) {
      selectedList?.length === 0 && setIsSelectMode(false);
    }
  }, [isSelectMode, selectedList, setIsSelectMode]);

  const thirdWidth =
    (bodyWidth - (numImages - 1) * theme.spacing.xs) / numImages;

  const getMarginRight = (i: number): number => {
    if ((i + 1) % numImages) {
      return theme.spacing.xs;
    } else {
      return 0;
    }
  };

  const isDocument = (item: Document | LocalFile): item is Document => {
    return item.__typename === 'Document';
  };

  const isLocalUri = (url?: string | null) =>
    !!url && (url.startsWith('file:') || url.startsWith('data:'));

  const handleDeleteFailed = (item: Document | LocalFile) => {
    if (item.__typename === 'Document') {
      const uploadFailedKeys = new Set(
        uploadingErrorFiles.map((d: any) => getDocKey(d))
      );
      setPendingUploads((prev) => {
        const next = new Map(prev);
        for (const key of next.keys()) {
          if (uploadFailedKeys.has(key)) next.delete(key);
        }
        return next;
      });

      // Simply remove from error files state
      setUploadingErrorFiles((prev) =>
        prev.filter((doc) => doc?.id !== item.id)
      );
    }
  };

  // ============================
  // ✅ Scheme B selection logic
  // ============================
  const [anchorIndex, setAnchorIndex] = useState<number | null>(null);

  const selectedKeySet = useMemo(() => {
    return new Set((selectedList ?? []).map((it: any) => getItemKey(it)));
  }, [selectedList]);

  const allFilesKeyMap = useMemo(() => {
    const m = new Map<string, Document | LocalFile>();
    allFiles.forEach((it: any) => m.set(getItemKey(it), it));
    return m;
  }, [allFiles]);

  const computeNextSelectedList = useCallback(
    (
      clicked: Document | LocalFile,
      index: number,
      e?: any,
      inSelectMode?: boolean
    ) => {
      const ne = e?.nativeEvent;
      const shiftKey = !!ne?.shiftKey;
      const ctrlOrCmdKey = !!ne?.ctrlKey || !!ne?.metaKey;

      const nextKeys = new Set(selectedKeySet);

      // Shift: range select (RESET to anchor->clicked)
      if (shiftKey && anchorIndex !== null) {
        const start = Math.min(anchorIndex, index);
        const end = Math.max(anchorIndex, index);

        nextKeys.clear();

        for (let k = start; k <= end; k++) {
          const it: any = allFiles[k];
          if (!it) continue;
          nextKeys.add(getItemKey(it));
        }

        setAnchorIndex(anchorIndex);
      }
      // Ctrl/Cmd: toggle single
      else if (ctrlOrCmdKey) {
        const key = getItemKey(clicked);
        if (nextKeys.has(key)) nextKeys.delete(key);
        else nextKeys.add(key);
        setAnchorIndex(index);
      }
      // ✅ Normal click:
      else {
        const key = getItemKey(clicked);

        if (inSelectMode) {
          if (nextKeys.has(key)) nextKeys.delete(key);
          else nextKeys.add(key);
        } else {
          nextKeys.clear();
          nextKeys.add(key);
        }

        setAnchorIndex(index);
      }

      const next: Array<Document | LocalFile> = [];
      allFiles.forEach((it: any) => {
        const key = getItemKey(it);
        if (nextKeys.has(key)) next.push(allFilesKeyMap.get(key) ?? it);
      });

      return next;
    },
    [allFiles, allFilesKeyMap, anchorIndex, selectedKeySet]
  );

  const handleSelectClick = useCallback(
    (item: Document | LocalFile, index: number, e?: any) => {
      !isSelectMode && setIsSelectMode(true);

      // Preferred: set selected list (Scheme B)
      if (onChangeSelectedList) {
        const inSelectMode = isSelectMode || (selectedList?.length ?? 0) > 0;
        const next = computeNextSelectedList(item, index, e, inSelectMode);
        onChangeSelectedList(next);
        return;
      }

      // Fallback: best-effort using single toggle (Shift will be imperfect)
      const ne = e?.nativeEvent;
      const shiftKey = !!ne?.shiftKey;
      // const ctrlOrCmdKey = !!ne?.ctrlKey || !!ne?.metaKey;

      if (shiftKey && anchorIndex !== null) {
        const start = Math.min(anchorIndex, index);
        const end = Math.max(anchorIndex, index);
        for (let k = start; k <= end; k++) {
          const it = allFiles[k] as any;
          if (!it) continue;
          // can't "only add" without set; this will toggle (imperfect)
          onSelect?.(it);
        }
        return;
      }

      // ctrl/cmd or normal -> toggle single
      onSelect?.(item);
      setAnchorIndex(index);

      // normal click "single only" can't be done without onChangeSelectedList
      // (so we don't attempt to clear others here)
    },
    [
      isSelectMode,
      setIsSelectMode,
      onChangeSelectedList,
      computeNextSelectedList,
      anchorIndex,
      allFiles,
      onSelect,
    ]
  );

  const [shiftDown, setShiftDown] = useState(false);

  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Shift') setShiftDown(true);
    };
    const onKeyUp = (ev: KeyboardEvent) => {
      if (ev.key === 'Shift') setShiftDown(false);
    };
    const onBlur = () => setShiftDown(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const hoverRange = useMemo(() => {
    if (!shiftDown || anchorIndex === null || isHoveredIndex < 0) return null;

    const start = Math.min(anchorIndex, isHoveredIndex);
    const end = Math.max(anchorIndex, isHoveredIndex);
    return { start, end };
  }, [shiftDown, anchorIndex, isHoveredIndex]);

  const isInHoverRange = (index: number) => {
    if (!hoverRange) return false;
    return index >= hoverRange.start && index <= hoverRange.end;
  };

  useEffect(() => {
    if (anchorIndex !== null) return;
    if (!selectedList?.length) return;

    const firstKey = getItemKey(selectedList[0] as any);
    const idx = allFiles.findIndex((x: any) => getItemKey(x) === firstKey);
    if (idx >= 0) setAnchorIndex(idx);
  }, [anchorIndex, selectedList, allFiles]);

  const isItemSelected = (item: Document | LocalFile) =>
    selectedKeySet.has(getItemKey(item));

  return (
    <Box
      flexDirection='row'
      flexWrap='wrap'
      onLayout={(e) => setBodyWidth(e.nativeEvent.layout.width)}>
      {allFiles.map((item: Document | LocalFile, i) => {
        const image = isDocument(item) ? item.file : item;
        const owner = isDocument(item) ? item.owner : me;
        const isLocal = isLocalUri(image?.url);

        const isFailed = uploadingErrorFiles.find(
          (f) => f?.clientId === (item as any).clientId
        );

        // ============================
        // Uploading local placeholder
        // ============================
        if (isLocal) {
          return (
            <Box
              key={image.id}
              marginBottom='xs'
              onMouseEnter={() => setIsHoveredIndex(i)}
              onMouseLeave={() => setIsHoveredIndex(-1)}>
              <Box
                width={thirdWidth}
                height={thirdWidth}
                overflow='hidden'
                borderRadius='xs'
                borderColor='grey01'
                borderWidth={1}
                alignItems='center'
                justifyContent='center'
                style={[
                  {
                    marginRight: getMarginRight(i),
                    marginBottom: theme.spacing.xxs,
                    backgroundColor: theme.colors.grey01,
                  },
                ]}>
                {isFailed ? (
                  <Box padding='s'>
                    <Text
                      variant='labelTabHeaderText'
                      color='textPrimary'
                      marginBottom='xs'>
                      Upload Failed
                    </Text>
                    <Box
                      flexDirection='row'
                      alignItems='center'
                      justifyContent='center'
                      gap='xs'
                      px='xs'>
                      <TouchableOpacity
                        onPress={() => onRetry?.(item)}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                        <Icon
                          name='RotateCcw'
                          variant='m'
                          width={16}
                          height={16}
                          padding='xxxs'
                          color='grey05'
                        />
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => handleDeleteFailed(item)}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                        <Icon
                          name='Trash2'
                          variant='m'
                          width={16}
                          height={16}
                          padding='xxxs'
                          color='grey05'
                        />
                      </TouchableOpacity>
                    </Box>
                  </Box>
                ) : (
                  <ActivityIndicator
                    color={theme.colors.textPrimary}
                    size={16}
                  />
                )}
              </Box>

              <Box flexDirection='row' alignItems='center'>
                <Avatar
                  id={owner?.id}
                  avatar={owner?.avatar}
                  label={owner?.name}
                  size='xxs'
                />
                <Box marginLeft='xxs'>
                  <Text variant='metadataSmall' style={{ flexGrow: 1 }}>
                    {owner?.name}
                  </Text>
                  <Text variant='metadataSmaller'>
                    {isDocument(item)
                      ? `${t('format:monthAndDayYear', {
                          val: item.createdAt,
                        })}`
                      : ''}
                  </Text>
                </Box>
              </Box>
            </Box>
          );
        }

        const isVideo = (item as any).isVideo;
        const isSelected = isItemSelected(item);

        // Generate accessibility label based on file type
        const getAccessibilityLabel = () => {
          if (item.isImage) {
            return `Media | Image | ${item.name}`;
          } else if (isVideo) {
            return `Media | Video | ${item.name}`;
          } else {
            return `File | Document | ${item.name}`;
          }
        };

        return (
          <Box
            key={getItemKey(item)}
            marginBottom='xs'
            onMouseEnter={() => setIsHoveredIndex(i)}
            onMouseLeave={() => setIsHoveredIndex(-1)}>
            <TouchableOpacity
              accessibilityLabel={getAccessibilityLabel()}
              onPress={(e: any) => {
                const ne = e?.nativeEvent;
                const shiftKey = !!ne?.shiftKey;
                const ctrlOrCmdKey = !!ne?.ctrlKey || !!ne?.metaKey;

                // If selecting or modifier keys pressed -> selection
                if (isSelectMode || shiftKey || ctrlOrCmdKey) {
                  handleSelectClick(item, i, e);
                  return;
                }

                // otherwise open/preview
                onPress?.(item);
              }}
              onLongPress={() => onLongPress?.(item)}
              style={[
                {
                  width: thirdWidth,
                  height: thirdWidth,
                  flexDirection: 'row',
                  marginRight: getMarginRight(i),
                  marginBottom: theme.spacing.xxs,
                  outlineStyle: 'none',
                  boxShadow: 'none',
                },
              ]}>
              <Box
                overflow='hidden'
                borderRadius='xs'
                borderColor='grey02'
                borderWidth={1}>
                {(item as any).isImage || isVideo ? (
                  <CachedImage
                    imageClientId={(item as any).clientId}
                    image={image}
                    width={thirdWidth}
                    height={thirdWidth}
                    isRemoveable={!!onDelete}
                    contentType={(item as any).contentType}
                    style={{ backgroundColor: 'white' }}
                  />
                ) : (
                  <Box
                    width={thirdWidth}
                    height={thirdWidth}
                    alignItems='center'
                    justifyContent='center'
                    style={{
                      backgroundColor: isSelected
                        ? theme.colors.grey02
                        : theme.colors.grey01,
                    }}>
                    <Icon
                      fileType={(item as any).contentType || undefined}
                      color='blue'
                      variant='xl'
                      marginBottom='xxs'
                      style={{
                        backgroundColor: isSelected
                          ? theme.colors.grey02
                          : theme.colors.grey01,
                      }}
                    />
                    <Text
                      variant='labelRequired'
                      numberOfLines={1}
                      maxWidth={thirdWidth - 16}
                      style={{
                        color: theme.colors.textPrimary,
                      }}>
                      {(item as any)?.name}
                    </Text>
                  </Box>
                )}

                {(isSelected || isHoveredIndex === i || isInHoverRange(i)) && (
                  <Box
                    width={thirdWidth}
                    height={thirdWidth}
                    style={{
                      ...StyleSheet.absoluteFillObject,
                      backgroundColor:
                        isSelected && ((item as any).isImage || isVideo)
                          ? 'rgba(0, 0, 0, 0.25)'
                          : 'rgba(0, 0, 0, 0.1)',
                    }}
                  />
                )}

                {isVideo && <PlayCircle />}

                {(isSelectMode ||
                  isHoveredIndex === i ||
                  isInHoverRange(i)) && (
                  <Box
                    style={{
                      ...StyleSheet.absoluteFillObject,
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                    }}>
                    <Box
                      position='absolute'
                      style={{
                        right: 8,
                        top: 8,
                      }}>
                      <RadioButton
                        iconDefault='StateNo'
                        iconColor={
                          isSelectMode && isHoveredIndex !== i
                            ? 'grey02'
                            : 'white'
                        }
                        iconVariant='m'
                        isSelected={!!isSelected}
                        // ✅ Important: RadioButton must pass event through
                        onPress={(e: any) => handleSelectClick(item, i, e)}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </TouchableOpacity>

            <Box flexDirection='row' alignItems='center'>
              <Avatar
                id={owner?.id}
                avatar={owner?.avatar}
                label={owner?.name}
                size='xxs'
              />
              <Box marginLeft='xxs'>
                <Text variant='metadataSmall' style={{ flexGrow: 1 }}>
                  {owner?.name}
                </Text>
                <Text variant='metadataSmaller'>
                  {isDocument(item)
                    ? `${t('format:monthAndDayYear', {
                        val: item.createdAt,
                      })}`
                    : ''}
                </Text>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ImageGridNew;
